import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/overlay/dialog';
import { GlassCard } from '@/components/ui/layout/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form/input';
import { Label } from '@/components/ui/form/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/form/select';
import { Textarea } from '@/components/ui/form/textarea';
import { Loader2, Upload, FileCheck, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useUploadActivityFile, useCreateManualActivity } from '@/hooks/useActivityMutations';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { log } from '@/lib/logger';

interface RecordActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Mode = 'upload' | 'manual';

const ALLOWED_EXTENSIONS = ['.fit', '.gpx', '.tcx'];
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type FileType = 'FIT' | 'GPX' | 'TCX';
const getFileType = (filename: string): FileType | null => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.fit')) return 'FIT';
  if (lower.endsWith('.gpx')) return 'GPX';
  if (lower.endsWith('.tcx')) return 'TCX';
  return null;
};
const formatFileSize = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

export function RecordActivityDialog({ open, onOpenChange }: RecordActivityDialogProps) {
  const { unitSystem } = useUnitSystem();
  const uploadMutation = useUploadActivityFile();
  const manualMutation = useCreateManualActivity();
  const distanceUnit = unitSystem === 'imperial' ? 'mi' : 'km';
  const elevationUnit = unitSystem === 'imperial' ? 'ft' : 'm';

  const [mode, setMode] = useState<Mode>('upload');
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manual state
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [time, setTime] = useState('');
  const [sport, setSport] = useState('');
  const [title, setTitle] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [distanceInput, setDistanceInput] = useState('');
  const [elevationGain, setElevationGain] = useState('');
  const [avgHeartRate, setAvgHeartRate] = useState('');
  const [perceivedEffort, setPerceivedEffort] = useState('');
  const [notes, setNotes] = useState('');

  const isPending = uploadMutation.isPending || manualMutation.isPending;

  const resetForm = () => {
    setError(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setTime('');
    setSport('');
    setTitle('');
    setDurationMinutes('');
    setDistanceInput('');
    setElevationGain('');
    setAvgHeartRate('');
    setPerceivedEffort('');
    setNotes('');
  };

  const handleClose = () => {
    if (!isPending) {
      resetForm();
      onOpenChange(false);
    }
  };

  // ---- Upload handlers ----
  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext))) return `Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} accepted.`;
    if (file.size > MAX_FILE_SIZE_BYTES) return `File too large. Maximum ${MAX_FILE_SIZE_MB}MB.`;
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;
    if (!files || files.length === 0) { setSelectedFile(null); return; }
    const file = files[0];
    const validationError = validateFile(file);
    if (validationError) { setError(validationError); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile || isPending) return;
    setError(null);
    uploadMutation.mutate(selectedFile, {
      onSuccess: (response) => {
        if (response.deduplicated) {
          toast({ title: 'Activity already exists' });
        } else {
          toast({ title: 'Activity uploaded successfully' });
        }
        resetForm();
        onOpenChange(false);
      },
      onError: (err) => {
        const axiosErr = err as { response?: { status?: number } };
        const s = axiosErr.response?.status;
        let msg = 'Upload failed, please try again';
        if (s === 400) msg = 'Invalid file format';
        else if (s === 413) msg = `File too large (max ${MAX_FILE_SIZE_MB}MB)`;
        else if (s === 422) msg = 'Could not parse activity file';
        else if (s === 429) msg = 'Too many uploads today';
        setError(msg);
        toast({ title: 'Upload failed', description: msg, variant: 'destructive' });
      },
    });
  };

  // ---- Manual handlers ----
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!sport) { setError('Sport is required'); return; }
    if (!durationMinutes || parseInt(durationMinutes, 10) <= 0) { setError('Duration must be a positive number'); return; }
    if (distanceInput && (isNaN(parseFloat(distanceInput)) || parseFloat(distanceInput) <= 0)) { setError('Distance must be a positive number'); return; }

    let distanceKm: number | null = null;
    if (distanceInput) {
      const val = parseFloat(distanceInput);
      distanceKm = unitSystem === 'imperial' ? val / 0.621371 : val;
    }

    let elevationM: number | null = null;
    if (elevationGain) {
      const val = parseFloat(elevationGain);
      elevationM = unitSystem === 'imperial' ? val / 3.28084 : val;
    }

    manualMutation.mutate(
      {
        date,
        time: time || null,
        sport,
        title: title.trim() || null,
        duration_minutes: parseInt(durationMinutes, 10),
        distance_km: distanceKm,
        elevation_gain_m: elevationM,
        avg_heart_rate: avgHeartRate ? parseInt(avgHeartRate, 10) : null,
        perceived_effort: perceivedEffort ? parseInt(perceivedEffort, 10) : null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          toast({ title: 'Activity recorded' });
          resetForm();
          onOpenChange(false);
        },
        onError: (err) => {
          log.error('ACTIVITIES', 'Failed to create manual activity:', err);
          const apiErr = err as { response?: { data?: { detail?: string } } };
          const msg = apiErr.response?.data?.detail || 'Failed to record activity';
          setError(msg);
          toast({ title: 'Error', description: msg, variant: 'destructive' });
        },
      },
    );
  };

  const tabClass = (active: boolean) => cn(
    'flex-1 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.12em] rounded-md transition-all duration-200 border',
    active
      ? 'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8]'
      : 'border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white/60',
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="p-0 bg-transparent border-none sm:max-w-[520px]">
        <GlassCard variant="raised" className="rounded-2xl">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle>Record Activity</DialogTitle>
              <DialogDescription>
                Upload a file from your device or enter details manually.
              </DialogDescription>
            </DialogHeader>

            {/* Tab Switcher */}
            <div className="flex gap-2 mt-4 mb-5">
              <button type="button" className={tabClass(mode === 'upload')} onClick={() => { setMode('upload'); setError(null); }}>
                <Upload className="h-3 w-3 inline mr-1.5 -mt-px" />Upload File
              </button>
              <button type="button" className={tabClass(mode === 'manual')} onClick={() => { setMode('manual'); setError(null); }}>
                Manual Entry
              </button>
            </div>

            {/* Upload Mode */}
            {mode === 'upload' && (
              <div className="space-y-3">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".fit,.gpx,.tcx"
                  onChange={handleFileChange}
                  disabled={isPending}
                  className="bg-white/[0.03] border-white/[0.1] font-mono text-sm min-h-[44px]"
                  aria-label="Select activity file (FIT, GPX, or TCX)"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  [SYS] ACCEPTED FORMATS: FIT, GPX, TCX — MAX {MAX_FILE_SIZE_MB}MB
                </p>

                {selectedFile && !error && (() => {
                  const fileType = getFileType(selectedFile.name);
                  return (
                    <div className="flex items-center gap-2 text-[10px] font-mono text-[#A0A0A0] bg-white/[0.03] border border-white/[0.06] px-3 py-2 rounded-md">
                      <FileCheck className="h-3.5 w-3.5 text-[#00FFC8] shrink-0" />
                      <span className="truncate">{selectedFile.name}</span>
                      <span className="text-white/30">{formatFileSize(selectedFile.size)}</span>
                      {fileType && <span className="text-[#00FFC8]">{fileType}</span>}
                    </div>
                  );
                })()}

                <DialogFooter className="pt-2">
                  <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>Cancel</Button>
                  <Button
                    type="button"
                    onClick={handleUpload}
                    disabled={!selectedFile || isPending}
                  >
                    {uploadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Upload
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Manual Entry Mode */}
            {mode === 'manual' && (
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rec-date">Date *</Label>
                    <Input id="rec-date" type="date" value={date} onChange={e => setDate(e.target.value)} required disabled={isPending} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rec-time">Time</Label>
                    <Input id="rec-time" type="time" value={time} onChange={e => setTime(e.target.value)} disabled={isPending} placeholder="Optional" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rec-sport">Sport *</Label>
                    <Select value={sport} onValueChange={setSport} disabled={isPending}>
                      <SelectTrigger id="rec-sport"><SelectValue placeholder="Select sport" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Run">Run</SelectItem>
                        <SelectItem value="Bike">Bike</SelectItem>
                        <SelectItem value="Swim">Swim</SelectItem>
                        <SelectItem value="Walk">Walk</SelectItem>
                        <SelectItem value="Hike">Hike</SelectItem>
                        <SelectItem value="Strength">Strength</SelectItem>
                        <SelectItem value="Crossfit">Crossfit</SelectItem>
                        <SelectItem value="Yoga">Yoga</SelectItem>
                        <SelectItem value="Triathlon">Triathlon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rec-duration">Duration (min) *</Label>
                    <Input id="rec-duration" type="number" step="1" min="1" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} required disabled={isPending} placeholder="e.g. 45" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rec-title">Title</Label>
                  <Input id="rec-title" type="text" value={title} onChange={e => setTitle(e.target.value)} disabled={isPending} placeholder="Optional — e.g. Morning easy run" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rec-distance">Distance ({distanceUnit})</Label>
                    <Input id="rec-distance" type="number" step="0.1" min="0" value={distanceInput} onChange={e => setDistanceInput(e.target.value)} disabled={isPending} placeholder="Optional" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rec-elevation">Elevation ({elevationUnit})</Label>
                    <Input id="rec-elevation" type="number" step="1" min="0" value={elevationGain} onChange={e => setElevationGain(e.target.value)} disabled={isPending} placeholder="Optional" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rec-hr">Avg Heart Rate</Label>
                    <Input id="rec-hr" type="number" step="1" min="30" max="250" value={avgHeartRate} onChange={e => setAvgHeartRate(e.target.value)} disabled={isPending} placeholder="Optional" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rec-rpe">Perceived Effort</Label>
                    <Select value={perceivedEffort} onValueChange={setPerceivedEffort} disabled={isPending}>
                      <SelectTrigger id="rec-rpe"><SelectValue placeholder="RPE 1-10" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                          <SelectItem key={n} value={String(n)}>{n} — {['Rest', 'Very easy', 'Easy', 'Light', 'Moderate', 'Somewhat hard', 'Hard', 'Very hard', 'Extremely hard', 'Max effort'][n - 1]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rec-notes">Notes</Label>
                  <Textarea id="rec-notes" value={notes} onChange={e => setNotes(e.target.value)} disabled={isPending} placeholder="How did it feel? Any details to remember..." rows={2} />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>Cancel</Button>
                  <Button type="submit" disabled={isPending}>
                    {manualMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Activity
                  </Button>
                </DialogFooter>
              </form>
            )}

            {error && (
              <div className="flex items-center gap-2 mt-3 text-[10px] font-mono text-[hsl(0,70%,55%)] bg-[hsl(0,70%,55%)]/[0.06] border border-[hsl(0,70%,55%)]/20 px-3 py-2 rounded-md">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </GlassCard>
      </DialogContent>
    </Dialog>
  );
}
